import { Calendar, Check } from 'lucide-react';
import SEO from '@/components/SEO';

const BOOKING_LINK = 'https://propelsports.myesoftplanner.com';
const SPORTS = ['Cricket', 'Baseball', 'Softball'];
const FEATURES = [
  { label: 'Bowling machine — $20 additional *', muted: true },
  { label: 'Adjustable speed', muted: true },
  { label: 'LED lighting' },
  { label: 'Climate controlled' },
];

export default function BattingTunnels() {
  return (
    <div data-testid="batting-tunnels-page" className="pt-[68px]">
      <SEO title="Batting Tunnels" description="Indoor batting tunnels for cricket, baseball, and softball at $40/hour. Bowling machine available. Book your session today!" path="/batting-tunnels" />

      {/* Hero */}
      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 70% 40%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Batting Tunnels</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Professional indoor batting tunnels for cricket, baseball, and softball. Perfect for individual practice or team sessions.</p>
        </div>
      </section>

      {/* Tunnel Booking — Horizontal Layout */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div data-testid="tunnel-booking" className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-10 hover:border-[#F26522]/30 hover:shadow-lg transition-all">
            <div className="flex flex-col md:flex-row md:items-center gap-8">
              {/* Left — Info */}
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-[#0B1D3A] mb-3">Tunnel Booking</h3>
                <div className="flex flex-wrap gap-2 mb-5">
                  {SPORTS.map(s => (
                    <span key={s} className="bg-[#F26522]/10 text-[#F26522] px-3 py-1 rounded-full text-xs font-medium">{s}</span>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {FEATURES.map(feat => (
                    <div key={feat.label} className={`flex items-center gap-2 text-sm ${feat.muted ? 'text-gray-400' : 'text-gray-600'}`}>
                      <Check size={14} className={`shrink-0 ${feat.muted ? 'text-gray-400' : 'text-[#F26522]'}`} />
                      {feat.label}
                    </div>
                  ))}
                </div>
                <p data-testid="bowling-machine-disclaimer" className="mt-4 text-red-600 text-xs font-medium leading-relaxed">
                  * Disclaimer: Bowling machines are NOT available by default. They will be made available and charged separately in a month's time.
                </p>
              </div>

              {/* Right — Price + CTA */}
              <div className="shrink-0 text-center md:text-right md:min-w-[200px]">
                <div className="text-5xl md:text-6xl font-bold text-[#F26522] leading-none">$40</div>
                <div className="text-gray-500 text-sm font-medium mt-1 mb-5">per hour / per tunnel</div>
                <a
                  href={BOOKING_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-tunnel-btn"
                  className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-8 py-3.5 rounded-lg font-semibold text-sm transition-all hover:shadow-lg hover:shadow-orange-500/20"
                >
                  <Calendar size={16} />
                  Book Tunnel
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Stats */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-6">Tunnel Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-sm">
              <div className="text-[#F26522] text-2xl font-bold mb-1">6</div>
              <div className="text-gray-500 text-sm">Total Tunnels</div>
            </div>
            <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-sm">
              <div className="text-[#F26522] text-2xl font-bold mb-1">$40</div>
              <div className="text-gray-500 text-sm">Per Hour</div>
            </div>
            <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-sm">
              <div className="text-[#F26522] text-2xl font-bold mb-1">Year-Round</div>
              <div className="text-gray-500 text-sm">Availability</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
